/**
 * Cola de comisión `/admin/solicitudes-acceso` (Sprint 9 + Z21).
 *
 * Migrada al design system Sprint 14 (bg-surface-*, text-text-*).
 * Modal aprobar (Z21): la comisión confirma `lote_id` real (dropdown
 * con lotes activos), `tipo_tenencia` (heredado del vínculo declarado,
 * editable) y `tenencia_desde` (heredado del solicitante, editable)
 * antes de aprobar. Sin Magic Link — el aprobado entra con OTP normal.
 */
import { useEffect, useState, type FormEvent } from 'react';
import { ApiError } from '../../lib/api/client';
import { listarLotes } from '../../lib/api/lotes';
import {
  aprobarSolicitud,
  listarSolicitudes,
  rechazarSolicitud,
} from '../../lib/api/solicitudes-acceso';
import type {
  EstadoSolicitudAcceso,
  LoteResumen,
  PlantillaRechazoAcceso,
  SolicitudAccesoRead,
  TipoTenencia,
  VinculoDeclarado,
} from '../../lib/api/types';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { InputFecha } from '../../components/ui/InputFecha';
import { formatFecha, formatRut } from '../../lib/format';
import { hoy } from '../../lib/tiempo';

const FILTROS: { valor: EstadoSolicitudAcceso | 'todas'; etiqueta: string }[] = [
  { valor: 'pendiente', etiqueta: 'En revisión' },
  // Sprint 18 P11: vocabulario validación. El estado interno sigue
  // siendo 'aprobada'/'rechazada' (no se renombra el enum); solo cambia
  // lo que ve el usuario.
  { valor: 'aprobada', etiqueta: 'Validadas' },
  { valor: 'rechazada', etiqueta: 'No validadas' },
  { valor: 'todas', etiqueta: 'Todas' },
];

const ETIQUETA_VINCULO: Record<VinculoDeclarado, string> = {
  propietario: 'Propietario',
  arrendatario: 'Arrendatario',
  familiar_propietario: 'Familiar de propietario',
  otro: 'Otro',
};

const ETIQUETA_TIPO_TENENCIA: Record<TipoTenencia, string> = {
  propietario_residente: 'Propietario residente',
  propietario_no_residente: 'Propietario no residente',
  arrendatario: 'Arrendatario',
  comodatario: 'Comodatario',
  otro: 'Otro',
};

const COLOR_ESTADO: Record<EstadoSolicitudAcceso, string> = {
  pendiente: 'bg-state-warning/15 text-state-warning border border-state-warning/30',
  aprobada: 'bg-state-success/15 text-state-success border border-state-success/30',
  rechazada: 'bg-state-danger/15 text-state-danger border border-state-danger/30',
};

// Mapeo razonable del `vinculo_declarado` del solicitante a un default
// de `tipo_tenencia`. La comisión puede ajustarlo en el modal antes de
// aprobar.
function defaultTipoDesdeVinculo(v: VinculoDeclarado): TipoTenencia {
  if (v === 'arrendatario') return 'arrendatario';
  if (v === 'otro') return 'otro';
  return 'propietario_residente';
}

const INPUT_CLASS =
  'w-full rounded-sm bg-bg-sunken border-[0.5px] border-white/15 text-text-primary px-3 py-2 text-sm focus:border-orange focus:ring-1 focus:ring-orange/30 placeholder:text-text-muted disabled:opacity-50';

interface ModalAprobarProps {
  solicitud: SolicitudAccesoRead;
  lotes: LoteResumen[];
  onClose: () => void;
  onAprobada: () => void;
}

function ModalAprobar({
  solicitud,
  lotes,
  onClose,
  onAprobada,
}: ModalAprobarProps) {
  // Busca un lote cuyo identificador coincida con el texto libre del
  // solicitante. Si no calza, la comisión debe elegirlo manualmente.
  const loteQueEscribio =
    lotes.find(
      (l) =>
        l.identificador.trim().toLowerCase() ===
        solicitud.lote_solicitado.trim().toLowerCase(),
    ) ?? null;

  // Casas donde ese RUT YA tiene tenencia activa. Si el solicitante
  // escribió otra, lo más probable es un dígito mal tipeado apuntando a
  // la casa del vecino de al lado (caso real 2026-09-01: la propietaria
  // de la 6978 escribió 6979, la casa de sus arrendatarios).
  const lotesDelPadron = solicitud.persona_existente_lotes ?? [];
  const hayDiscrepanciaCasa =
    lotesDelPadron.length > 0 &&
    !lotesDelPadron.some(
      (ident) =>
        ident.trim().toLowerCase() ===
        solicitud.lote_solicitado.trim().toLowerCase(),
    );

  // Mismo criterio que el email de abajo: cuando los datos se contradicen
  // NO se pre-llena, porque pre-llenar sesga una decisión que entrega
  // acceso a una casa — y el dato preseleccionado sería el menos
  // confiable de los dos (texto libre tipeado por el solicitante).
  const lotePreseleccionado = hayDiscrepanciaCasa
    ? ''
    : (loteQueEscribio?.id ?? '');

  const [loteId, setLoteId] = useState(lotePreseleccionado);
  // Confirmación explícita cuando se aprueba en una casa distinta de
  // aquella donde el RUT ya figura. El backend responde 409 sin esto.
  const [confirmaCasaDistinta, setConfirmaCasaDistinta] = useState(false);
  // Fase 4: nombre confirmado. Pre-llena con el de la persona pre-cargada (la
  // fuente de verdad por RUT) si existe; si no, con lo que escribió el
  // solicitante. La comisión normaliza/completa antes de aprobar.
  const [nombre, setNombre] = useState(
    solicitud.persona_existente_nombre ?? solicitud.nombre_completo,
  );
  // El correo se vuelve el login de la cuenta al aprobar. Si el que
  // escribió el solicitante y el del padrón DIFIEREN, el campo arranca
  // VACÍO y hay que elegir: pre-llenar cualquiera de los dos sesga una
  // decisión que entrega acceso a una casa. El del solicitante puede ser
  // de otra persona (caso real casa 6938) y el del padrón puede estar
  // obsoleto o ser del cónyuge, porque se cargó por CSV. Si coinciden —o
  // no hay padrón— no hay nada que decidir y se pre-llena.
  const emailPadron = solicitud.persona_existente_email;
  const hayDiscrepanciaEmail =
    emailPadron != null &&
    emailPadron.trim().toLowerCase() !== solicitud.email.trim().toLowerCase();
  const [email, setEmail] = useState(
    hayDiscrepanciaEmail ? '' : (emailPadron ?? solicitud.email),
  );
  const [tipoTenencia, setTipoTenencia] = useState<TipoTenencia>(
    defaultTipoDesdeVinculo(solicitud.vinculo_declarado),
  );
  const [tenenciaDesde, setTenenciaDesde] = useState(
    solicitud.tenencia_desde ?? '',
  );
  const [responsable, setResponsable] = useState(true);
  const [comentario, setComentario] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const valido =
    loteId !== '' &&
    tenenciaDesde !== '' &&
    nombre.trim().length > 0 &&
    email.trim().length > 0;



  // Fase 4: ¿el nombre que escribió el solicitante difiere del pre-cargado?
  // (normalizando mayúsculas/espacios). Señal de variante o suplantación.
  const nombrePreCargado = solicitud.persona_existente_nombre;
  const nombreDifiere =
    nombrePreCargado != null &&
    nombrePreCargado.trim().toLowerCase() !==
      solicitud.nombre_completo.trim().toLowerCase();

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!valido || enviando) return;
    setEnviando(true);
    setError(null);
    try {
      await aprobarSolicitud(solicitud.id, {
        rol_asignado: 'vecino',
        lote_id: loteId,
        confirmar_casa_distinta: confirmaCasaDistinta,
        tipo_tenencia: tipoTenencia,
        tenencia_desde: tenenciaDesde,
        responsable_pago_cuotas: responsable,
        nombre_completo: nombre.trim() || null,
        email: email.trim() || null,
        comentario: comentario.trim() || null,
      });
      onAprobada();
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? typeof err.body === 'object' &&
            err.body !== null &&
            'detail' in err.body
            ? String((err.body as { detail: unknown }).detail)
            : `Error ${err.status}`
          : 'No pudimos aprobar la solicitud. Intenta de nuevo.';
      setError(msg);
      setEnviando(false);
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50"
      role="dialog"
      aria-modal="true"
    >
      <Card className="max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="text-lg font-medium text-text-primary">
              Revisar solicitud de acceso
            </h3>
            <p className="text-xs text-text-secondary mt-0.5">
              {solicitud.nombre_completo} · RUT {formatRut(solicitud.rut)}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-text-muted hover:text-text-primary text-xl leading-none"
            aria-label="Cerrar"
          >
            ×
          </button>
        </div>

        <div className="text-xs text-text-secondary bg-orange/[0.08] border-l-4 border-orange p-3 rounded-sm mb-4">
          Confirma los datos. Al aprobar se crea la cuenta, se liga al
          lote como tenencia activa y el vecino podrá entrar al portal
          con su correo.
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <div className="text-sm">
              <span className="text-text-muted">Ingresó el solicitante: </span>
              <span className="text-text-primary">
                {solicitud.nombre_completo}
              </span>
            </div>
            {nombrePreCargado != null && (
              <div className="text-sm">
                <span className="text-text-muted">
                  Ya en sistema (RUT {formatRut(solicitud.rut)}):{' '}
                </span>
                <span className="text-text-primary">{nombrePreCargado}</span>
              </div>
            )}
            {nombreDifiere && (
              <div
                className="text-xs text-state-warning bg-state-warning/[0.1] border-l-4 border-state-warning p-2 rounded-sm"
                role="alert"
              >
                ⚠ El nombre ingresado no coincide con el que ya está en sistema
                para ese RUT. Verifica que sea la misma persona antes de
                aprobar (¿alguien usando el RUT de un tercero?).
              </div>
            )}
            <label className="block text-sm space-y-1">
              <span className="text-text-secondary">
                Nombre confirmado{' '}
                <span className="text-text-muted text-xs">
                  (se guarda como nombre oficial de la persona)
                </span>
              </span>
              <input
                type="text"
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                maxLength={200}
                className={INPUT_CLASS}
                required
                disabled={enviando}
              />
            </label>
          </div>

          <div className="space-y-2">
            <div className="text-sm">
              <span className="text-text-muted">Correo que escribió: </span>
              <span className="text-text-primary">{solicitud.email}</span>
            </div>
            {solicitud.persona_existente_email != null && (
              <div className="text-sm">
                <span className="text-text-muted">Ya en sistema: </span>
                <span className="text-text-primary">
                  {solicitud.persona_existente_email}
                </span>
              </div>
            )}
            {hayDiscrepanciaEmail && (
              <div
                className="text-xs text-state-warning bg-state-warning/[0.1] border-l-4 border-state-warning p-2 rounded-sm"
                role="alert"
              >
                ⚠ Los dos correos no coinciden. Con el que elijas va a
                iniciar sesión, así que confirma cuál es el suyo antes de
                aprobar.
              </div>
            )}
            {hayDiscrepanciaEmail && (
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="ghost"
                  type="button"
                  onClick={() => setEmail(solicitud.email)}
                  disabled={enviando}
                >
                  Usar el que escribió
                </Button>
                <Button
                  variant="ghost"
                  type="button"
                  onClick={() => setEmail(emailPadron ?? '')}
                  disabled={enviando}
                >
                  Usar el del sistema
                </Button>
              </div>
            )}
            <label className="block text-sm space-y-1">
              <span className="text-text-secondary">
                Correo confirmado{' '}
                <span className="text-text-muted text-xs">
                  (con este entra al portal)
                </span>
              </span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                maxLength={255}
                className={INPUT_CLASS}
                required
                disabled={enviando}
              />
            </label>
          </div>

          {hayDiscrepanciaCasa && (
            <div
              role="alert"
              className="rounded-md border border-state-danger bg-state-danger/10 p-3 space-y-2"
            >
              <p className="text-sm font-bold text-state-danger">
                ⚠️ Ojo: la casa no coincide. Revisa bien.
              </p>
              <p className="text-sm text-text-primary">
                Este RUT ya figura en la{' '}
                <strong>
                  casa {lotesDelPadron.join(', ')}
                </strong>
                , pero escribió{' '}
                <strong>"{solicitud.lote_solicitado}"</strong>.
              </p>
              <p className="text-xs text-text-secondary">
                Lo más probable es un error de tipeo. Aprobar en la casa
                equivocada le entrega acceso al estado de cuenta y a los
                residentes de otro vecino. Confirma con el solicitante antes
                de continuar.
              </p>
              <label className="flex items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={confirmaCasaDistinta}
                  onChange={(e) => setConfirmaCasaDistinta(e.target.checked)}
                  disabled={enviando}
                  className="mt-0.5"
                />
                <span className="text-text-primary">
                  Verifiqué que corresponde a una casa distinta de la que ya
                  tiene (por ejemplo, es dueño de más de una).
                </span>
              </label>
            </div>
          )}

          <label className="block text-sm space-y-1">
            <span className="text-text-secondary">
              Casa{' '}
              <span className="text-text-muted text-xs">
                (solicitante escribió: "{solicitud.lote_solicitado}")
              </span>
            </span>
            <select
              value={loteId}
              onChange={(e) => setLoteId(e.target.value)}
              className={INPUT_CLASS}
              required
              disabled={enviando}
            >
              <option value="">— elige —</option>
              {lotes.map((l) => (
                <option key={l.id} value={l.id}>
                  Casa {l.identificador}
                  {lotesDelPadron.includes(l.identificador)
                    ? ' — ya figura aquí'
                    : ''}
                </option>
              ))}
            </select>
          </label>

          <label className="block text-sm space-y-1">
            <span className="text-text-secondary">
              Tipo de tenencia{' '}
              <span className="text-text-muted text-xs">
                (vínculo declarado:{' '}
                {ETIQUETA_VINCULO[solicitud.vinculo_declarado]})
              </span>
            </span>
            <select
              value={tipoTenencia}
              onChange={(e) => setTipoTenencia(e.target.value as TipoTenencia)}
              className={INPUT_CLASS}
              disabled={enviando}
            >
              {(Object.keys(ETIQUETA_TIPO_TENENCIA) as TipoTenencia[]).map(
                (t) => (
                  <option key={t} value={t}>
                    {ETIQUETA_TIPO_TENENCIA[t]}
                  </option>
                ),
              )}
            </select>
          </label>

          <label className="block text-sm space-y-1">
            <span className="text-text-secondary">
              Fecha desde
              {solicitud.tenencia_desde && (
                <span className="text-text-muted text-xs">
                  {' '}
                  (solicitante: {formatFecha(solicitud.tenencia_desde)})
                </span>
              )}
            </span>
            <InputFecha
              value={tenenciaDesde}
              onChange={setTenenciaDesde}
              className={INPUT_CLASS}
              max={hoy()}
              requerido
              disabled={enviando}
            />
          </label>

          <label className="flex items-start gap-2 text-sm text-text-secondary">
            <input
              type="checkbox"
              checked={responsable}
              onChange={(e) => setResponsable(e.target.checked)}
              className="mt-1"
              disabled={enviando}
            />
            <span>
              Responsable de pago de cuotas
              <span className="block text-xs text-text-muted">
                Marca esto si es el titular que paga las cuotas del barrio.
                Solo puede haber un responsable activo por casa.
              </span>
            </span>
          </label>

          <label className="block text-sm space-y-1">
            <span className="text-text-secondary">
              Comentario (opcional)
            </span>
            <textarea
              value={comentario}
              onChange={(e) => setComentario(e.target.value)}
              className={INPUT_CLASS}
              rows={2}
              disabled={enviando}
              placeholder="Notas internas de la comisión."
            />
          </label>

          {error && (
            <div
              className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary"
              role="alert"
            >
              {error}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="ghost"
              type="button"
              onClick={onClose}
              disabled={enviando}
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={!valido || enviando}
            >
              {enviando ? 'Validando…' : 'Validar'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}

interface ModalRechazarProps {
  solicitud: SolicitudAccesoRead;
  onClose: () => void;
  onRechazada: () => void;
}

function ModalRechazar({
  solicitud,
  onClose,
  onRechazada,
}: ModalRechazarProps) {
  const [comentario, setComentario] = useState('');
  const [plantilla, setPlantilla] =
    useState<PlantillaRechazoAcceso>('estandar');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!comentario.trim()) {
      setError('El comentario es obligatorio para rechazar.');
      return;
    }
    setEnviando(true);
    setError(null);
    try {
      await rechazarSolicitud(solicitud.id, {
        comentario: comentario.trim(),
        plantilla_email: plantilla,
      });
      onRechazada();
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? typeof err.body === 'object' &&
            err.body !== null &&
            'detail' in err.body
            ? String((err.body as { detail: unknown }).detail)
            : `Error ${err.status}`
          : 'No pudimos rechazar la solicitud. Intenta de nuevo.';
      setError(msg);
      setEnviando(false);
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50"
      role="dialog"
      aria-modal="true"
    >
      <Card className="max-w-md w-full">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="text-lg font-medium text-text-primary">
              No validar solicitud
            </h3>
            <p className="text-xs text-text-secondary mt-0.5">
              {solicitud.nombre_completo} · Casa {solicitud.lote_solicitado}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-text-muted hover:text-text-primary text-xl leading-none"
            aria-label="Cerrar"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <label className="block text-sm space-y-1">
            <span className="text-text-secondary">
              Motivo del rechazo{' '}
              <span className="text-state-danger">*</span>
            </span>
            <textarea
              value={comentario}
              onChange={(e) => setComentario(e.target.value)}
              className={INPUT_CLASS}
              rows={3}
              required
              disabled={enviando}
              placeholder="Explica por qué se rechaza (queda en auditoría)."
            />
          </label>

          <label className="block text-sm space-y-1">
            <span className="text-text-secondary">Respuesta al vecino</span>
            <select
              value={plantilla}
              onChange={(e) =>
                setPlantilla(e.target.value as PlantillaRechazoAcceso)
              }
              className={INPUT_CLASS}
              disabled={enviando}
            >
              <option value="estandar">
                Estándar — no se pudo aprobar, contacta a la Comisión
              </option>
              <option value="solo_titular">
                El titular administra el acceso — explica que él lo registra
                como residente
              </option>
              <option value="ya_tiene_cuenta">
                Ya tiene cuenta — explica que entra con el enlace a su correo
              </option>
            </select>
            {/* El motivo NUNCA se envía (puede tener info sensible de otra
                persona y un correo se reenvía). Conviene que el operador lo
                sepa acá y no lo descubra después. */}
            <span className="block text-xs text-text-muted">
              El motivo que escribes arriba queda en auditoría y{' '}
              <strong>no se le envía</strong> al vecino. Lo que recibe es el
              texto de la respuesta elegida.
            </span>
          </label>

          {error && (
            <div
              className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary"
              role="alert"
            >
              {error}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="ghost"
              type="button"
              onClick={onClose}
              disabled={enviando}
            >
              Cancelar
            </Button>
            <Button
              variant="danger"
              type="submit"
              disabled={enviando}
            >
              {enviando ? 'Rechazando…' : 'Confirmar rechazo'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}

interface FilaSolicitudProps {
  solicitud: SolicitudAccesoRead;
  onAprobar: () => void;
  onRechazar: () => void;
}

function FilaSolicitud({
  solicitud,
  onAprobar,
  onRechazar,
}: FilaSolicitudProps) {
  const color = COLOR_ESTADO[solicitud.estado];
  // H12: el nombre autoritativo es el de la Persona en sistema (confirmado por
  // la comisión al validar); si no hay match por RUT, el ingresado.
  const nombreMostrar =
    solicitud.persona_existente_nombre ?? solicitud.nombre_completo;

  // H11: validadas / no validadas van en fila compacta (sin acciones); solo
  // las pendientes muestran la card completa con datos + botones.
  if (solicitud.estado !== 'pendiente') {
    return (
      <Card className="py-2.5">
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
          <div className="min-w-0">
            <span className="text-sm font-medium text-text-primary">
              {nombreMostrar}
            </span>
            <span className="text-xs text-text-muted ml-2">
              Casa{' '}
              <span className="font-mono">{solicitud.lote_solicitado}</span>
              {solicitud.revisado_at &&
                ` · ${formatFecha(solicitud.revisado_at.slice(0, 10))}`}
            </span>
          </div>
          <span
            className={`text-[11px] uppercase tracking-wide font-medium px-2.5 py-1 rounded ${color}`}
          >
            {solicitud.estado}
          </span>
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <header className="flex flex-wrap items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          {/* Pendiente: el nombre INGRESADO (la comparación con el de sistema
              vive en el modal de validación). H12 aplica a las compactas. */}
          <h3 className="text-base font-medium text-text-primary">
            {solicitud.nombre_completo}
          </h3>
          <p className="text-xs text-text-muted mt-0.5">
            Solicitado el {formatFecha(solicitud.solicitado_at.slice(0, 10))}
            {' · '}
            Casa <span className="font-mono">{solicitud.lote_solicitado}</span>
          </p>
        </div>
        <span
          className={`text-[11px] uppercase tracking-wide font-medium px-2.5 py-1 rounded ${color}`}
        >
          {solicitud.estado}
        </span>
      </header>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm mb-3">
        <div>
          <p className="text-xs text-text-muted">RUT</p>
          <p className="text-text-primary font-mono">{formatRut(solicitud.rut)}</p>
        </div>
        <div>
          <p className="text-xs text-text-muted">Vínculo declarado</p>
          <p className="text-text-primary">
            {ETIQUETA_VINCULO[solicitud.vinculo_declarado]}
          </p>
        </div>
        <div>
          <p className="text-xs text-text-muted">Email</p>
          <p className="text-text-primary break-all">{solicitud.email}</p>
        </div>
        <div>
          <p className="text-xs text-text-muted">Teléfono</p>
          <p className="text-text-primary font-mono">
            {solicitud.telefono ? `+56 ${solicitud.telefono}` : '—'}
          </p>
        </div>
        {solicitud.tenencia_desde && (
          <div>
            <p className="text-xs text-text-muted">Desde</p>
            <p className="text-text-primary">
              {formatFecha(solicitud.tenencia_desde)}
            </p>
          </div>
        )}
      </div>

      {/* Historial del RUT: una solicitud repetida se veía idéntica a una
          primera vez. Si ya fue aprobada antes, casi siempre significa que
          su cuenta nunca se creó y volvió a pedir — no que esté
          insistiendo. */}
      {solicitud.previas_aprobadas > 0 && (
        <div
          className="mb-3 p-2 bg-state-warning/[0.1] border-l-4 border-state-warning rounded-sm text-xs text-text-primary"
          role="alert"
        >
          ⚠ Este RUT ya tuvo{' '}
          {solicitud.previas_aprobadas === 1
            ? 'una solicitud aprobada'
            : `${solicitud.previas_aprobadas} solicitudes aprobadas`}
          . Revisa si ya tiene acceso antes de aprobar otra vez.
        </div>
      )}
      {solicitud.previas_aprobadas === 0 &&
        solicitud.previas_rechazadas > 0 && (
          <div className="mb-3 p-2 bg-bg-sunken rounded-sm text-xs text-text-secondary border-l-2 border-text-muted/30">
            Este RUT tuvo {solicitud.previas_rechazadas}{' '}
            {solicitud.previas_rechazadas === 1
              ? 'solicitud rechazada'
              : 'solicitudes rechazadas'}{' '}
            antes.
          </div>
        )}

      {solicitud.comentario && (
        <div className="mb-3 p-2 bg-bg-sunken rounded-sm text-xs text-text-secondary border-l-2 border-text-muted/30">
          <span className="font-medium text-text-primary">Comentario:</span>{' '}
          {solicitud.comentario}
        </div>
      )}

      <div className="flex justify-end gap-2 border-t border-white/[0.08] pt-3">
        <button
          type="button"
          onClick={onRechazar}
          className="text-xs text-state-danger hover:underline px-3"
        >
          No validar
        </button>
        <Button variant="primary" size="sm" onClick={onAprobar}>
          Revisar
        </Button>
      </div>
    </Card>
  );
}

export function ColaSolicitudes() {
  const [filtro, setFiltro] = useState<EstadoSolicitudAcceso | 'todas'>(
    'pendiente',
  );
  const [solicitudes, setSolicitudes] = useState<SolicitudAccesoRead[] | null>(
    null,
  );
  const [lotes, setLotes] = useState<LoteResumen[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [accion, setAccion] = useState<
    | { tipo: 'aprobar'; solicitud: SolicitudAccesoRead }
    | { tipo: 'rechazar'; solicitud: SolicitudAccesoRead }
    | null
  >(null);
  const [toast, setToast] = useState<string | null>(null);

  async function cargar() {
    setLoading(true);
    setError(null);
    try {
      const data = await listarSolicitudes(filtro);
      setSolicitudes(data);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? `Error ${err.status}: no pudimos cargar la cola.`
          : 'No pudimos cargar la cola. Intenta recargar.',
      );
    } finally {
      setLoading(false);
    }
  }

  // Carga lotes una vez (los necesita el modal aprobar).
  useEffect(() => {
    listarLotes(true)
      .then((data) => setLotes(data))
      .catch(() => setLotes([]));
  }, []);

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtro]);

  function handleAprobada() {
    setAccion(null);
    setToast('Solicitud aprobada. El vecino ya puede entrar al portal.');
    cargar();
  }

  function handleRechazada() {
    setAccion(null);
    setToast('Solicitud rechazada.');
    cargar();
  }

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  return (
    <div className="px-4 md:px-8 py-6 space-y-5">
      <div>
        <h2 className="text-xl font-medium text-text-primary">
          Solicitudes de acceso
        </h2>
        <p className="text-sm text-text-secondary mt-0.5">
          Vecinos que pidieron acceso al portal vía formulario público.
        </p>
      </div>

      <div className="flex flex-wrap gap-2 border-b border-white/[0.08]">
        {FILTROS.map((f) => (
          <button
            key={f.valor}
            type="button"
            onClick={() => setFiltro(f.valor)}
            className={`px-3 py-2 text-sm border-b-2 -mb-px transition-colors ${
              filtro === f.valor
                ? 'border-orange text-text-primary font-medium'
                : 'border-transparent text-text-muted hover:text-text-secondary'
            }`}
          >
            {f.etiqueta}
          </button>
        ))}
      </div>

      {loading && (
        <div className="space-y-3" aria-busy="true">
          {[0, 1].map((i) => (
            <Card key={i}>
              <div className="h-4 w-40 bg-bg-sunken rounded mb-3" />
              <div className="h-3 w-60 bg-bg-sunken rounded" />
            </Card>
          ))}
        </div>
      )}

      {error && !loading && (
        <div className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary">
          {error}
        </div>
      )}

      {!loading && !error && solicitudes && solicitudes.length === 0 && (
        <Card>
          <p className="text-sm text-text-muted">
            No hay solicitudes en este estado.
          </p>
        </Card>
      )}

      {!loading && !error && solicitudes && solicitudes.length > 0 && (
        <div className="space-y-3">
          {solicitudes.map((s) => (
            <FilaSolicitud
              key={s.id}
              solicitud={s}
              onAprobar={() => setAccion({ tipo: 'aprobar', solicitud: s })}
              onRechazar={() => setAccion({ tipo: 'rechazar', solicitud: s })}
            />
          ))}
        </div>
      )}

      {accion?.tipo === 'aprobar' && (
        <ModalAprobar
          solicitud={accion.solicitud}
          lotes={lotes}
          onClose={() => setAccion(null)}
          onAprobada={handleAprobada}
        />
      )}
      {accion?.tipo === 'rechazar' && (
        <ModalRechazar
          solicitud={accion.solicitud}
          onClose={() => setAccion(null)}
          onRechazada={handleRechazada}
        />
      )}

      {toast && (
        <div className="fixed bottom-4 right-4 bg-bg-elevated border-[0.5px] border-white/15 text-text-primary px-4 py-2 rounded-sm shadow-lg text-sm z-50">
          {toast}
        </div>
      )}
    </div>
  );
}
